import { useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

type ConnectionHealth = "connected" | "warning" | "degraded" | "disconnected" | "verifying";

interface ConnectionSummary {
  id: string;
  type: string;
  label: string;
  health_status: ConnectionHealth;
  last_delivery_at: string | null;
  last_error: string | null;
}

interface CommunicationHubProps {
  connections: ConnectionSummary[];
  onReconnect: (id: string) => void;
  onAddConnection: () => void;
}

const HEALTH_CONFIG: Record<ConnectionHealth, { label: string; color: string; icon: React.ReactNode }> = {
  connected:    { label: "Connected",    color: "text-status-up",   icon: <CheckCircle2 className="h-3 w-3" /> },
  warning:      { label: "Warning",      color: "text-yellow-500",  icon: <AlertTriangle className="h-3 w-3" /> },
  degraded:     { label: "Degraded",     color: "text-orange-500",  icon: <AlertTriangle className="h-3 w-3" /> },
  disconnected: { label: "Disconnected", color: "text-status-down", icon: <XCircle className="h-3 w-3" /> },
  verifying:    { label: "Verifying",    color: "text-blue-400",    icon: <Loader2 className="h-3 w-3 animate-spin" /> },
};

export function CommunicationHub({ connections, onReconnect, onAddConnection }: CommunicationHubProps) {
  const needsAttention = connections.filter(c =>
    c.health_status === "disconnected" || c.health_status === "degraded"
  );
  const healthy = connections.filter(c =>
    c.health_status === "connected" || c.health_status === "warning" || c.health_status === "verifying"
  );

  return (
    <div className="space-y-0">
      {/* Connections */}
      <section className="border-b-2 border-foreground/10 pb-8 mb-8">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-primary">Connections</h3>
            <p className="text-[9px] text-muted-foreground uppercase font-bold tracking-widest mt-0.5">
              {connections.length} channel{connections.length !== 1 ? "s" : ""} configured
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onAddConnection}
            className="h-7 rounded-none border-2 border-foreground bg-background font-black uppercase text-[9px] gap-1.5 shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
          >
            <Plus className="h-3 w-3" />
            Add Channel
          </Button>
        </div>

        {connections.length === 0 ? (
          <div className="border-2 border-dashed border-foreground/20 p-6 text-center">
            <p className="text-[10px] font-black uppercase text-muted-foreground">No channels connected yet.</p>
            <p className="text-[9px] text-muted-foreground uppercase mt-1">Connect Slack, Teams, Jira, or Email below.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {connections.map(conn => {
              const health = HEALTH_CONFIG[conn.health_status];
              return (
                <div
                  key={conn.id}
                  className="flex items-center justify-between border-2 border-foreground/10 px-4 py-2.5 bg-accent/5"
                >
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center gap-1.5 ${health.color}`}>
                      {health.icon}
                      <span className="text-[9px] font-black uppercase">{health.label}</span>
                    </div>
                    <span className="text-[10px] font-black uppercase">{conn.label}</span>
                  </div>
                  <div className="text-right">
                    {conn.last_delivery_at && (
                      <p className="text-[9px] text-muted-foreground uppercase font-bold">
                        Last delivery: {new Date(conn.last_delivery_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Needs Attention */}
      {needsAttention.length > 0 && (
        <section className="border-b-2 border-foreground/10 pb-8 mb-8">
          <h3 className="text-xs font-black uppercase tracking-[0.2em] text-status-down mb-4">Needs Attention</h3>
          <div className="space-y-2">
            {needsAttention.map(conn => (
              <div
                key={conn.id}
                className="flex items-center justify-between border-2 border-status-down/20 bg-status-down/5 px-4 py-3"
              >
                <div className="space-y-0.5">
                  <p className="text-[10px] font-black uppercase">{conn.label}</p>
                  <p className="text-[9px] text-muted-foreground uppercase font-bold">
                    {conn.last_error ?? "Connection issue detected."}
                    {conn.last_delivery_at && ` — Last delivery: ${new Date(conn.last_delivery_at).toLocaleDateString()}`}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReconnect(conn.id)}
                  className="h-7 rounded-none border-2 border-foreground font-black uppercase text-[9px] shadow-brutal-sm hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all"
                >
                  Reconnect
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
